'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/brands', label: 'Brands' },
  { href: '/gallery', label: 'Gallery' },
  { href: '/visit', label: 'Visit Us' },
  { href: '/contact', label: 'Contact' },
];

export default function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header className="bg-white border-b border-[#E0DDD8]">
        {/* Top bar: logo centred */}
        <div className="flex items-center justify-between py-5 px-6 border-b border-[#E0DDD8]">
          {/* Spacer for mobile hamburger alignment */}
          <div className="w-10 md:hidden" />

          <div className="flex-1 flex justify-center">
            <Link href="/">
              <Image
                src="/assets/images/Screenshot_2026-09-05_102730-Photoroom-1788600471111.png"
                alt="Amanda Jones Jewellery"
                width={160}
                height={70}
                className="object-contain"
                priority
              />
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-[5px] p-2 text-[#2B2B2B]"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-[1.5px] bg-[#2B2B2B] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-[6.5px]' : ''}`} />
            <span className={`block w-6 h-[1.5px] bg-[#2B2B2B] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-[1.5px] bg-[#2B2B2B] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-[6.5px]' : ''}`} />
          </button>

          {/* Desktop: empty right spacer to keep logo centred */}
          <div className="hidden md:block w-10" />
        </div>

        {/* Nav links row — desktop only */}
        <nav className="hidden md:flex items-center justify-center gap-8 py-3 px-6">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`font-sans text-[0.68rem] font-semibold tracking-[0.14em] uppercase transition-colors duration-200 hover:text-[#5FA828] ${
                pathname === link?.href ? 'text-[#5FA828]' : 'text-[#2B2B2B]'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>
      </header>

      {/* Mobile Menu */}
      <div
        className={`mobile-menu ${menuOpen ? 'open' : ''}`}
        style={{ zIndex: 99 }}
      >
        <div className="flex items-center justify-between mb-12 mt-4">
          <Image
            src="/assets/images/Screenshot_2026-09-05_102730-Photoroom-1788600471111.png"
            alt="Amanda Jones Jewellery logo"
            width={100}
            height={50}
            className="object-contain"
          />
          <button onClick={() => setMenuOpen(false)} className="text-[#2B2B2B] p-2" aria-label="Close menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {navLinks?.map((link, i) => (
            <Link
              key={link?.href}
              href={link?.href}
              className="font-display text-3xl font-light text-[#2B2B2B] py-3 border-b border-[#E0DDD8] hover:text-[#5FA828] transition-colors"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {link?.label}
            </Link>
          ))}
        </div>
        <div className="mt-auto pt-8">
          <Link href="/contact" className="btn-primary w-full text-center">
            Visit Us in Cowbridge
          </Link>
          <div className="flex items-center gap-4 mt-6">
            <a
              href="https://www.instagram.com/amanda13.jewellery/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2B2B2B] hover:text-[#5FA828] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
